import type { ComposeMode, MailboxRef } from '@momomail/shared';
import type { RouteSpec } from './affordkit';

/**
 * The app's pages as 馍馍 knows them (AffordKit routes), the same as the phone's (apps/mobile/lib/afford/agent.dart): where the user is
 * goes with each chat message, and the agent can take them to another. Here only what a route means; the app goes there (screen.ts).
 */
export type ScreenPlace =
  | { page: 'mail'; box: string }
  | { page: 'message'; id: number }
  | { page: 'compose'; mode: ComposeMode; id: number | null }
  | { page: 'calendar' }
  | { page: 'settings'; section: string };

export function screenRoutes(settingsSections: readonly string[]): RouteSpec[] {
  return [
    {
      path: '/mail/:box',
      description: 'The mail list, showing a mailbox',
      params: {
        box: {
          description:
            "inbox (all inboxes), unread, flagged, folder-<id> (a folder: ids in the folders list, mail.folders), tag-important / tag-reply / tag-low / tag-junk, tag-label:<name> (one of the user's own tags), search-<words>",
        },
      },
    },
    { path: '/message/:id', description: 'A mail (its conversation), by message id: reply, forward, archive, delete from here' },
    { path: '/compose/new', description: 'Writing a new email' },
    { path: '/reply/:id', description: 'Writing a reply to a message' },
    { path: '/reply-all/:id', description: 'Writing a reply to everyone on a message' },
    { path: '/forward/:id', description: 'Forwarding a message' },
    { path: '/draft/:id', description: 'Carrying on with a draft from the Drafts folder' },
    { path: '/calendar', description: 'The calendar' },
    { path: '/settings/:section', description: 'Settings', params: { section: { values: [...settingsSections] } } },
  ];
}

/** The route of each way of writing (its first part). */
const COMPOSE_ROUTES: Record<ComposeMode, string> = { new: 'compose', reply: 'reply', replyAll: 'reply-all', forward: 'forward', draft: 'draft' };

/** What a route is (null: no page of this app). */
export function parseRoute(route: string): ScreenPlace | null {
  const [first, ...rest] = route.split('/').filter(Boolean);
  const arg = rest.join('/');
  const id = /^\d+$/.test(arg) ? Number(arg) : null;
  if (first === 'mail' && arg) return { page: 'mail', box: arg };
  if (first === 'message' && id !== null) return { page: 'message', id };
  if (first === 'compose' && arg === 'new') return { page: 'compose', mode: 'new', id: null };
  const mode = (Object.keys(COMPOSE_ROUTES) as ComposeMode[]).find((m) => m !== 'new' && COMPOSE_ROUTES[m] === first);
  if (mode && id !== null) return { page: 'compose', mode, id };
  if (first === 'calendar' && !arg) return { page: 'calendar' };
  if (first === 'settings' && arg) return { page: 'settings', section: arg };
  return null;
}

/** A place as its route (what parseRoute reads back). */
export function routeOf(place: ScreenPlace): string {
  switch (place.page) {
    case 'mail':
      return `/mail/${place.box}`;
    case 'message':
      return `/message/${place.id}`;
    case 'compose':
      return place.mode === 'new' || place.id === null ? '/compose/new' : `/${COMPOSE_ROUTES[place.mode]}/${place.id}`;
    case 'calendar':
      return '/calendar';
    case 'settings':
      return `/settings/${place.section}`;
  }
}

/** A mailbox as it goes in a route (/mail/:box). */
export function boxOf(ref: MailboxRef): string {
  switch (ref.kind) {
    case 'folder':
      return `folder-${ref.folderId}`;
    case 'tag':
      return `tag-${ref.tag}`;
    case 'search':
      return `search-${encodeURIComponent(ref.query)}`;
    case 'unified':
      return ref.view;
  }
}

/** The mailbox a route names; throws (with what is wrong, for the agent) when there is none. */
export function refOfBox(box: string, folders: readonly { id: number }[]): MailboxRef {
  if (box.startsWith('folder-')) {
    const id = Number(box.slice(7));
    if (!folders.some((f) => f.id === id)) throw new Error(`No folder ${box} (the folders and their ids are in mail.folders)`);
    return { kind: 'folder', folderId: id };
  }
  if (box.startsWith('tag-')) {
    const tag = box.slice(4);
    if (tag === 'important' || tag === 'reply' || tag === 'low' || tag === 'junk') return { kind: 'tag', tag };
    if (tag.startsWith('label:') && tag.length > 6) return { kind: 'tag', tag: tag as `label:${string}` };
    throw new Error(`No tag ${box}`);
  }
  if (box.startsWith('search-')) {
    let query = box.slice(7);
    try {
      query = decodeURIComponent(query);
    } catch {
      /* typed as it is, not encoded */
    }
    if (!query.trim()) throw new Error('Say what to search for: search-<words>');
    return { kind: 'search', query: query.trim(), accountId: null };
  }
  if (box === 'inbox' || box === 'unread' || box === 'flagged') return { kind: 'unified', view: box };
  throw new Error(`No mailbox ${box}`);
}
